import { gql } from "@apollo/client";
import { AccountFragment } from "fragments/account";

export const PageFragment = gql`
  fragment PageFragment on Page {
    id
    pageId
    title

    route

    # Which of its site's declared languages this page is written in. In the
    # fragment for the same reason everything below it is: the editor submits
    # the whole value object, so a field it never loaded is one the form cannot
    # show and cannot edit. (No backticks in here, for the reason the config
    # comment below gives. PageModel.update's undefined-guard is what keeps an
    # OLDER bundle that does not select this field from blanking it.)
    locale

    # The name this page's translations share. In the fragment for the same
    # reason locale is: the editor submits the whole value object.
    translationKey

    # This page's override of its site's document shell. In the fragment for the
    # same reason the elements below are: the page editor sends the whole value
    # object back on save, so a field it loaded without would be blanked by the
    # next save of anything else on the page. (No backticks in here: this is a
    # template literal, and one would end it.)
    config

    # Every screen that reads a page reads its elements, including the edit
    # form, which sends the whole value object back. Leaving them out of the
    # fragment would make saving a page's TITLE clear its content: absent from
    # what the form loaded, and absent from what it submitted.
    elements {
      elementid
      name
      type
      tags
      frame
      config

      # Whether this language's copy is still the prose it was translated from.
      # In the fragment for the same reason everything beside it is — the editor
      # sends the whole value object back, so a field it loaded without would be
      # cleared on the first save. It is also what the Elements tab's mark reads
      # and what its button writes.
      translationPending
    }

    # Preview parameters, saved with the page. The editor sends the whole value
    # object back on save, so a field it loaded without would be cleared — the
    # same reason the elements above are in this fragment. (No backticks in
    # here: this is a template literal, and one would end it.)
    editor {
      params {
        name
        value
      }
    }

    status

    # What visitors see, beside what the editor holds. Three answers computed
    # by the API off the row (no read) — see PagePublished in the api schema.
    # In the fragment because every screen that names a page wants them: the
    # listing's badge, the page screen's Publish button and the link out, which
    # goes to the PUBLISHED route and not the draft's. Read-only: none of the
    # three is on any input shape, so extraction drops them from a save.
    published {
      versionId
      route
      publishedAt
    }
    served
    changed

    createdAt
    updatedAt
  }
`;

// One kept version, as the page screen lists it. No id: a version is owned by
// its page and is only ever read as part of one.
export const PageVersionFragment = gql`
  fragment PageVersionFragment on PageVersion {
    versionId
    title
    route
    publishedAt
    current
  }
`;

// Every document reaches a page THROUGH its site. That nesting is the tenancy
// boundary rather than a stylistic choice — the API has no path to a page that
// does not resolve a site from the caller's own organization first.
export const get = gql`
  query PageGet($siteId: String!, $pageId: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        sites {
          id
          get(siteId: $siteId) {
            id
            pages {
              id
              get(pageId: $pageId) {
                ...PageFragment
              }
            }
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${PageFragment}
`;

export const list = gql`
  query PageList($siteId: String!, $input: ListInput) {
    viewer {
      id
      account {
        id
        sites {
          id
          get(siteId: $siteId) {
            id
            pages {
              id
              list(input: $input) {
                id
                last
                items {
                  ...PageFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${PageFragment}
`;

export const create = gql`
  mutation PageNew($siteId: String!, $input: PageInputCreate!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            pages {
              create(input: $input) {
                ...PageFragment
              }
            }
          }
        }
      }
    }
  }
  ${PageFragment}
`;

export const edit = gql`
  mutation PageEdit(
    $siteId: String!
    $pageId: String!
    $input: PageInputUpdate!
  ) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            pages {
              get(pageId: $pageId) {
                update(input: $input) {
                  ...PageFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${PageFragment}
`;

export const translate = gql`
  mutation PageTranslate(
    $siteId: String!
    $pageId: String!
    $input: PageInputTranslate!
  ) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            pages {
              get(pageId: $pageId) {
                translate(input: $input) {
                  ...PageFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${PageFragment}
`;

// This page and every other language of it, as a file.
//
// A query, and always driven lazily (`useLazyQuery`): the document is the whole
// group's elements and shell, which is the largest thing this API returns about
// a page, and nothing on any screen displays it. It is fetched when somebody
// asks for the file and never as part of rendering one.
//
// `fetchPolicy: "no-cache"` at the call site rather than here — a bundle is a
// snapshot, and it is also the largest thing this API says about a page, so it
// is neither read from the cache nor written into it.
export const exportBundle = gql`
  query PageExport($siteId: String!, $pageId: String!) {
    viewer {
      id
      account {
        id
        sites {
          id
          get(siteId: $siteId) {
            id
            pages {
              id
              export(pageId: $pageId)
            }
          }
        }
      }
    }
  }
`;

// Making the file new pages — the LISTING's verb. Where import is invoked
// decides what it does: this one creates a complete, independent copy of the
// file's group, new pageIds and a new translation group.
//
// The result is the PLAN — one row per language, its action and the fields
// that would change — and it is the same shape whether this was a dry run or a
// real one, because it is the same call. See PageImportResult in the api
// schema.
export const importBundle = gql`
  mutation PageImport($siteId: String!, $input: PageInputImport!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            pages {
              import(input: $input) {
                dryRun
                pages {
                  pageId
                  title
                  route
                  locale
                  action
                  changed
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Putting an edited file back into ONE page and its group — the verb on the
// page's own screen. The server checks the file is that page's data
// (page.import.page.mismatch otherwise), which is why this goes through
// \`get(pageId)\` rather than the namespace: the scope is the mutation's
// parent.
export const importPage = gql`
  mutation PageImportInto(
    $siteId: String!
    $pageId: String!
    $input: PageInputImport!
  ) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            pages {
              get(pageId: $pageId) {
                import(input: $input) {
                  dryRun
                  pages {
                    pageId
                    title
                    route
                    locale
                    action
                    changed
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// The page with its kept versions — the one query that selects \`versions\`,
// because that field reads the versions table and nothing but the screen that
// lists them wants it paid for. The page's own fields ride along so the
// screen's Publish button and the state sentence answer from one entry.
export const versions = gql`
  query PageVersions($siteId: String!, $pageId: String!) {
    viewer {
      id
      account {
        id
        sites {
          id
          get(siteId: $siteId) {
            id
            pages {
              id
              get(pageId: $pageId) {
                ...PageFragment
                versions {
                  ...PageVersionFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${PageFragment}
  ${PageVersionFragment}
`;

// Serve the draft. Returns the page WITH its versions, so the list on the
// screen that pressed the button updates from the mutation's own answer —
// the entity merges by id, and \`versions\` is a field of it.
export const publish = gql`
  mutation PagePublish($siteId: String!, $pageId: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            pages {
              get(pageId: $pageId) {
                publish {
                  ...PageFragment
                  versions {
                    ...PageVersionFragment
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${PageFragment}
  ${PageVersionFragment}
`;

// Serve a kept version instead, leaving the draft alone. Same answer shape as
// publish, for the same reason.
export const rollback = gql`
  mutation PageRollback(
    $siteId: String!
    $pageId: String!
    $input: PageInputRollback!
  ) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            pages {
              get(pageId: $pageId) {
                rollback(input: $input) {
                  ...PageFragment
                  versions {
                    ...PageVersionFragment
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${PageFragment}
  ${PageVersionFragment}
`;

export const remove = gql`
  mutation PageRemove($siteId: String!, $pageId: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            pages {
              get(pageId: $pageId) {
                remove
              }
            }
          }
        }
      }
    }
  }
`;

// The literal below must be a substring of what PageQueries.id resolves to on
// the API side (api/code/src/schema/account/page). That id is now site-scoped —
// `${site.id}/pages/queries` — so two sites' listings are two cache entries
// rather than one the second site quietly overwrites; this still matches both.
// If the two drift, a create succeeds and every list screen keeps serving the
// pre-create page with no error anywhere.
// api/code/test/schema.test.js pins the other half.
export const update = ({ pageId }) => (cache) => {

  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes(`pages/queries`)) {
      cache.data.delete(key);
    }
  });
};
